import React, { useEffect, useMemo, useState } from 'react';
import { cn } from '../../design-system/cn';
import { Modal, Button, Alert, TextInput, SelectInput, InputField, LockboxModalHeader, Card } from '../ui';
import { typography } from '../../design-system/tokens';
import { inferOpponentSide } from '../../utils/customPropSides';
import StakeChipRow from '../challenges/StakeChipRow';
import LockPreviewCard from '../challenges/LockPreviewCard';

const LOCK_TYPES = [
  { id: 'team-wins', emoji: '🏀', label: 'Team Wins', description: 'Lakers win the game', pick: 'Lakers win' },
  { id: 'player-points', emoji: '🏀', label: 'Player Scores More', description: 'LeBron scores more points than Durant', pick: 'LeBron scores more' },
  { id: 'first-score', emoji: '⚾', label: 'First Team To Score', description: 'Yankees score first', pick: 'Yankees score first' },
  { id: 'over-under', emoji: '🏈', label: 'Over / Under', description: 'Lakers score over 110 points', pick: 'Over 110' },
  { id: 'fighter-wins', emoji: '🥊', label: 'Fighter Wins', description: 'Fighter A wins by decision', pick: 'Fighter A wins' },
  { id: 'custom', emoji: '➕', label: 'Custom', description: '', pick: '' },
];

const TYPE_LABELS = Object.fromEntries(LOCK_TYPES.map((t) => [t.id, t.label]));

export default function CustomWagerCreateModal({
  open,
  onClose,
  customWagerError,
  customDescription,
  onDescriptionChange,
  customCreatorPick,
  onCreatorPickChange,
  customStake,
  onStakeChange,
  maxWagerPerPlayer,
  customOpponentId,
  onOpponentChange,
  challengeMode = 'specific',
  onChallengeModeChange,
  wagerEligibleFriends,
  blockedPlayers,
  playerStats,
  getRiskTally,
  escrowName,
  subscriptionProfile,
  onSubmit,
  submitDisabled,
  groupContext = null,
}) {
  const [step, setStep] = useState(1);
  const [activeTemplateId, setActiveTemplateId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setActiveTemplateId(null);
  }, [open]);

  const opponentPickPreview = useMemo(
    () => inferOpponentSide(customCreatorPick) || '—',
    [customCreatorPick]
  );
  const selectedOpponent = wagerEligibleFriends.find((f) => f.id === customOpponentId);
  const isOpenChallenge = challengeMode === 'open';
  const isGroupLock = Boolean(groupContext?.id);
  const groupLabel = groupContext?.name || 'your group';
  const stake = Number(customStake) || 0;

  const applyTemplate = (template) => {
    setActiveTemplateId(template.id);
    if (template.id === 'custom') {
      onDescriptionChange('');
      onCreatorPickChange('');
      return;
    }
    onDescriptionChange(template.description);
    onCreatorPickChange(template.pick);
  };

  const canAdvanceStep1 = Boolean(activeTemplateId);
  const canAdvanceStep2 = Boolean(customDescription.trim() && customCreatorPick.trim());
  const canAdvanceStep3 = stake > 0 && stake <= maxWagerPerPlayer
    && (isOpenChallenge || customOpponentId);

  const previewRows = useMemo(() => {
    const rows = [
      { label: 'Type', value: TYPE_LABELS[activeTemplateId] || 'Custom' },
      { label: 'Lock', value: customDescription.trim() || '—' },
      { label: 'Your side', value: customCreatorPick.trim() || '—', highlight: true },
      { label: 'Opponent side', value: opponentPickPreview },
      { label: 'Stake', value: stake > 0 ? `$${stake.toFixed(2)}` : '—' },
      {
        label: 'Opponent',
        value: isOpenChallenge
          ? (isGroupLock ? `${groupLabel} — waiting for member` : 'Open Arena — waiting for opponent')
          : (selectedOpponent?.name || 'Select a friend'),
      },
    ];
    if (stake > 0) {
      rows.push({ label: 'Challenge pool', value: `$${(stake * 2).toFixed(2)}` });
    }
    return rows;
  }, [
    activeTemplateId,
    customDescription,
    customCreatorPick,
    opponentPickPreview,
    stake,
    isOpenChallenge,
    isGroupLock,
    groupLabel,
    selectedOpponent,
  ]);

  const stepTitle = ['Choose lock type', 'Lock details', 'Stake & opponent', 'Review your lock'][step - 1];

  return (
    <Modal
      open={open}
      onClose={onClose}
      accent="brand"
      size="md"
      panelClassName="rounded-[2.5rem]"
      bodyClassName="p-6 md:p-8 max-h-[90vh] overflow-y-auto"
      header={(
        <LockboxModalHeader
          emoji="🎯"
          iconClassName="bg-emerald-500/10 border-emerald-500/20"
          title="Create custom lock"
          subtitle={`Step ${step} of 4 · ${stepTitle}`}
          onClose={onClose}
        />
      )}
    >
      {customWagerError && (
        <Alert tone="error" className="text-xs font-bold mb-4">
          {customWagerError}
        </Alert>
      )}

      {step === 1 && (
        <div className="space-y-3">
          <p className={`${typography.microWide} text-emerald-400 mb-1`}>Step 1 · Choose lock type</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {LOCK_TYPES.map((template) => {
              const active = activeTemplateId === template.id;
              return (
                <Card
                  key={template.id}
                  as="button"
                  type="button"
                  variant={active ? 'subtle' : 'raised'}
                  padding="md"
                  onClick={() => applyTemplate(template)}
                  className={cn(
                    'w-full text-left transition-all',
                    active
                      ? 'border-emerald-500/60 bg-emerald-500/15 shadow-[0_0_12px_rgba(16,185,129,0.2)]'
                      : 'hover:border-emerald-500/35'
                  )}
                >
                  <span className="text-xl block mb-2" aria-hidden>{template.emoji}</span>
                  <span className="block text-xs font-black uppercase tracking-tight text-white">
                    {template.label}
                  </span>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <p className={`${typography.microWide} text-emerald-400 mb-1`}>Step 2 · Lock details</p>
          <InputField label="What needs to happen?" className="mt-0">
            <textarea
              value={customDescription}
              onChange={(e) => {
                setActiveTemplateId('custom');
                onDescriptionChange(e.target.value);
              }}
              placeholder='e.g. "Lakers score over 110.5 points"'
              rows={3}
              className="mt-1 w-full bg-black/50 border border-gray-800 rounded-xl px-4 py-3 text-sm font-bold text-white placeholder-gray-600 focus:outline-none focus:border-emerald-500/50 transition-colors resize-none"
            />
          </InputField>
          <InputField label="What side are you taking?" className="mt-0">
            <TextInput
              type="text"
              value={customCreatorPick}
              onChange={(e) => {
                setActiveTemplateId('custom');
                onCreatorPickChange(e.target.value);
              }}
              placeholder='e.g. "Over 110.5"'
              focus="brand"
              className="mt-1 bg-black/50 border-gray-800 focus:border-emerald-500/50 text-sm font-bold"
            />
            <p className="text-[9px] text-gray-500 mt-1">
              Your opponent accepts the opposite side.
            </p>
          </InputField>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <p className={`${typography.microWide} text-emerald-400 mb-1`}>Step 3 · Stake</p>
          <StakeChipRow value={customStake} onChange={onStakeChange} max={maxWagerPerPlayer} />

          <p className={`${typography.microWide} text-gray-500 mt-4 mb-2`}>Who are you challenging?</p>
          {!isGroupLock && (
          <div className="flex flex-col sm:flex-row gap-2">
            <Card
              type="button"
              as="button"
              variant={!isOpenChallenge ? 'subtle' : 'raised'}
              padding="sm"
              onClick={() => onChallengeModeChange?.('specific')}
              className={cn(
                'flex-1 text-left transition-colors',
                !isOpenChallenge
                  ? 'border-emerald-500/50 bg-emerald-500/10'
                  : 'hover:border-gray-700'
              )}
            >
              <p className="text-[10px] font-black uppercase tracking-widest text-white">Challenge Friend</p>
              <p className="text-[9px] text-gray-500 mt-0.5">Send to someone you know</p>
            </Card>
            <Card
              type="button"
              as="button"
              variant={isOpenChallenge ? 'subtle' : 'raised'}
              padding="sm"
              onClick={() => onChallengeModeChange?.('open')}
              className={cn(
                'flex-1 text-left transition-colors',
                isOpenChallenge
                  ? 'border-emerald-500/50 bg-emerald-500/10'
                  : 'hover:border-gray-700'
              )}
            >
              <p className="text-[10px] font-black uppercase tracking-widest text-white">Open Arena</p>
              <p className="text-[9px] text-gray-500 mt-0.5">Post publicly</p>
            </Card>
          </div>
          )}

          {isOpenChallenge ? (
            <Card
              variant="subtle"
              padding="md"
              className={cn(
                'rounded-xl',
                isGroupLock ? 'border-violet-500/30 bg-violet-500/10' : 'border-emerald-500/30 bg-emerald-500/10'
              )}
            >
              <p className="text-xs text-gray-400 leading-relaxed">
                {isGroupLock
                  ? `Your lock posts to ${groupLabel}. Only league members can see and accept it.`
                  : 'Your lock posts to Open Arena. The first eligible player to accept takes the opposite side.'}
              </p>
            </Card>
          ) : (
            <InputField label="Challenge player" className="mt-0">
              <SelectInput
                value={customOpponentId}
                onChange={(e) => onOpponentChange(e.target.value)}
                className="mt-1 bg-black/50 border-gray-800 focus:border-emerald-500/50 text-sm font-bold"
              >
                <option value="">Select a player</option>
                {wagerEligibleFriends.length === 0 ? (
                  <option value="" disabled>No eligible friends</option>
                ) : (
                  wagerEligibleFriends.map((f) => {
                    const stats = playerStats[f.id] ?? { wins: 0, losses: 0 };
                    const riskTally = getRiskTally(f.id, f.riskTally);
                    const riskLabel = riskTally > 0
                      ? ` • Strike ${['I', 'II', 'III'][Math.min(riskTally, 3) - 1] || riskTally}`
                      : '';
                    return (
                      <option key={f.id} value={f.id}>
                        {f.name} — W-L {stats.wins ?? 0}-{stats.losses ?? 0}{riskLabel}
                      </option>
                    );
                  })
                )}
              </SelectInput>
              {blockedPlayers.length > 0 && (
                <p className="text-[9px] text-gray-500 mt-1">
                  Blocked players cannot be challenged.
                </p>
              )}
            </InputField>
          )}
        </div>
      )}

      {step === 4 && (
        <div className="space-y-4">
          <p className={`${typography.microWide} text-emerald-400 mb-1`}>Step 4 · Review</p>
          <LockPreviewCard
            rows={previewRows}
            stake={stake}
            subscriptionProfile={subscriptionProfile}
          />
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4">
            <p className={`${typography.microWide} text-amber-400 mb-1`}>Important</p>
            <p className="text-[10px] text-gray-400 leading-relaxed">
              Both players must agree on the lock. Funds go into{' '}
              <span className="text-emerald-400 font-bold">{escrowName}</span> until settlement.
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-3 mt-6">
        {step < 4 ? (
          <>
            <Button
              type="button"
              fullWidth
              disabled={
                (step === 1 && !canAdvanceStep1)
                || (step === 2 && !canAdvanceStep2)
                || (step === 3 && !canAdvanceStep3)
              }
              onClick={() => setStep((s) => Math.min(4, s + 1))}
              className="py-4 rounded-2xl min-h-0 text-[10px] uppercase tracking-widest"
            >
              Continue
            </Button>
            {step > 1 && (
              <Button
                type="button"
                variant="secondary"
                fullWidth
                onClick={() => setStep((s) => Math.max(1, s - 1))}
                className="py-3 rounded-2xl min-h-0"
              >
                Back
              </Button>
            )}
          </>
        ) : (
          <Button
            type="button"
            fullWidth
            disabled={submitDisabled}
            onClick={onSubmit}
            className="py-4 rounded-2xl min-h-0 text-[10px] uppercase tracking-widest"
          >
            {isOpenChallenge ? (isGroupLock ? 'Post group lock' : 'Post to Open Arena') : 'Send Lock'}
          </Button>
        )}
        <Button type="button" variant="ghost" fullWidth onClick={onClose} className="py-3 rounded-2xl min-h-0 text-xs text-gray-500">
          Cancel
        </Button>
      </div>
    </Modal>
  );
}
